import React, { useEffect, useRef, useState, useCallback } from "react";
import { Check, X, RotateCcw, Loader2, Crop } from "lucide-react";

// Solve 8 unknowns for a projective transform mapping dest(u,v) -> src(x,y)
function solveHomography(dst, src) {
  const A = [], b = [];
  for (let i = 0; i < 4; i++) {
    const [u, v] = dst[i], [x, y] = src[i];
    A.push([u, v, 1, 0, 0, 0, -u * x, -v * x]); b.push(x);
    A.push([0, 0, 0, u, v, 1, -u * y, -v * y]); b.push(y);
  }
  // Gaussian elimination (8x8)
  for (let i = 0; i < 8; i++) {
    let p = i;
    for (let r = i + 1; r < 8; r++) if (Math.abs(A[r][i]) > Math.abs(A[p][i])) p = r;
    [A[i], A[p]] = [A[p], A[i]]; [b[i], b[p]] = [b[p], b[i]];
    const piv = A[i][i] || 1e-9;
    for (let r = 0; r < 8; r++) {
      if (r === i) continue;
      const f = A[r][i] / piv;
      for (let c = i; c < 8; c++) A[r][c] -= f * A[i][c];
      b[r] -= f * b[i];
    }
  }
  const h = new Array(8);
  for (let i = 0; i < 8; i++) h[i] = b[i] / (A[i][i] || 1e-9);
  return h; // [a,b,c,d,e,f,g,h]
}

export default function CropEditor({ dataUrl, onApply, onCancel }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const dispW = useRef(0); const dispH = useRef(0); const scale = useRef(1);
  const corners = useRef([]); // display coords [TL,TR,BR,BL]
  const drag = useRef(-1);

  const draw = useCallback(() => {
    const c = canvasRef.current, img = imgRef.current; if (!c || !img) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.drawImage(img, 0, 0, dispW.current, dispH.current);
    const pts = corners.current;
    ctx.strokeStyle = "#00F0FF"; ctx.lineWidth = 2; ctx.fillStyle = "rgba(0,240,255,0.10)";
    ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
    for (let i = 1; i < 4; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.closePath(); ctx.fill(); ctx.stroke();
    for (const [x, y] of pts) {
      ctx.beginPath(); ctx.arc(x, y, 9, 0, Math.PI * 2); ctx.fillStyle = "#00F0FF"; ctx.fill();
      ctx.beginPath(); ctx.arc(x, y, 4, 0, Math.PI * 2); ctx.fillStyle = "#030712"; ctx.fill();
    }
  }, []);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const maxW = Math.min(520, img.width);
      scale.current = maxW / img.width;
      dispW.current = Math.round(img.width * scale.current);
      dispH.current = Math.round(img.height * scale.current);
      const c = canvasRef.current; c.width = dispW.current; c.height = dispH.current;
      const ix = dispW.current * 0.06, iy = dispH.current * 0.06;
      corners.current = [[ix, iy], [dispW.current - ix, iy], [dispW.current - ix, dispH.current - iy], [ix, dispH.current - iy]];
      setReady(true); draw();
    };
    img.src = dataUrl;
  }, [dataUrl, draw]);

  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return [(t.clientX - r.left) * (canvasRef.current.width / r.width), (t.clientY - r.top) * (canvasRef.current.height / r.height)]; };
  const down = (e) => { const [x, y] = pos(e); let best = -1, bd = 26; corners.current.forEach((p, i) => { const d = Math.hypot(p[0] - x, p[1] - y); if (d < bd) { bd = d; best = i; } }); drag.current = best; e.preventDefault(); };
  const move = (e) => { if (drag.current < 0) return; const [x, y] = pos(e); corners.current[drag.current] = [Math.max(0, Math.min(dispW.current, x)), Math.max(0, Math.min(dispH.current, y))]; draw(); e.preventDefault(); };
  const up = () => { drag.current = -1; };

  const reset = () => { const ix = dispW.current * 0.06, iy = dispH.current * 0.06; corners.current = [[ix, iy], [dispW.current - ix, iy], [dispW.current - ix, dispH.current - iy], [ix, dispH.current - iy]]; draw(); };

  const apply = async () => {
    setBusy(true);
    try {
      const img = imgRef.current;
      const src = corners.current.map(([x, y]) => [x / scale.current, y / scale.current]);
      const wTop = Math.hypot(src[1][0] - src[0][0], src[1][1] - src[0][1]);
      const wBot = Math.hypot(src[2][0] - src[3][0], src[2][1] - src[3][1]);
      const hL = Math.hypot(src[3][0] - src[0][0], src[3][1] - src[0][1]);
      const hR = Math.hypot(src[2][0] - src[1][0], src[2][1] - src[1][1]);
      let outW = Math.round((wTop + wBot) / 2), outH = Math.round((hL + hR) / 2);
      const cap = 1600; const k = Math.min(1, cap / Math.max(outW, outH)); outW = Math.max(1, Math.round(outW * k)); outH = Math.max(1, Math.round(outH * k));
      const dst = [[0, 0], [outW, 0], [outW, outH], [0, outH]];
      const h = solveHomography(dst, src);
      // source pixels
      const sc = document.createElement("canvas"); sc.width = img.width; sc.height = img.height;
      const sctx = sc.getContext("2d"); sctx.drawImage(img, 0, 0); const sdata = sctx.getImageData(0, 0, img.width, img.height).data;
      const out = document.createElement("canvas"); out.width = outW; out.height = outH;
      const octx = out.getContext("2d"); const odata = octx.createImageData(outW, outH); const od = odata.data;
      const [a, b, cc, d, e, f, g, hh] = h;
      for (let v = 0; v < outH; v++) {
        for (let u = 0; u < outW; u++) {
          const den = g * u + hh * v + 1;
          let sx = (a * u + b * v + cc) / den, sy = (d * u + e * v + f) / den;
          sx = Math.max(0, Math.min(img.width - 1, sx)); sy = Math.max(0, Math.min(img.height - 1, sy));
          const x0 = Math.floor(sx), y0 = Math.floor(sy), x1 = Math.min(img.width - 1, x0 + 1), y1 = Math.min(img.height - 1, y0 + 1);
          const fx = sx - x0, fy = sy - y0;
          const o = (v * outW + u) * 4;
          for (let ch = 0; ch < 3; ch++) {
            const p00 = sdata[(y0 * img.width + x0) * 4 + ch], p10 = sdata[(y0 * img.width + x1) * 4 + ch];
            const p01 = sdata[(y1 * img.width + x0) * 4 + ch], p11 = sdata[(y1 * img.width + x1) * 4 + ch];
            od[o + ch] = (p00 * (1 - fx) + p10 * fx) * (1 - fy) + (p01 * (1 - fx) + p11 * fx) * fy;
          }
          od[o + 3] = 255;
        }
      }
      octx.putImageData(odata, 0, 0);
      onApply(out.toDataURL("image/jpeg", 0.92));
    } catch (err) { onCancel(); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="crop-editor">
      <div className="bg-[#071018] border border-[#00F0FF44] rounded-2xl p-4 max-w-[90vw] max-h-[92vh] overflow-auto">
        <div className="flex items-center gap-2 mb-3">
          <Crop className="w-4 h-4 text-[#00F0FF]" />
          <p className="nx-orbitron font-black tracking-widest text-[#00F0FF] text-sm">CROP & STRAIGHTEN</p>
          <button onClick={onCancel} data-testid="crop-cancel" className="ml-auto text-cyan-200/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-cyan-100/45 text-[11px] nx-mono mb-2">Drag the 4 dots to the corners of the paper.</p>
        <div className="flex justify-center">
          <canvas ref={canvasRef} data-testid="crop-canvas" className="rounded-lg touch-none bg-black" style={{ touchAction: "none" }}
            onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={up}
            onTouchStart={down} onTouchMove={move} onTouchEnd={up} />
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={reset} data-testid="crop-reset" className="px-3 py-2 rounded-xl nx-mono text-xs text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11] flex items-center gap-1"><RotateCcw className="w-4 h-4" /> Reset</button>
          <button onClick={apply} disabled={busy || !ready} data-testid="crop-apply" className="flex-1 py-2 rounded-xl nx-orbitron font-bold text-[#030712] bg-[#00F0FF] disabled:opacity-40 flex items-center justify-center gap-2">{busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Straightening…</> : <><Check className="w-4 h-4" /> Apply crop</>}</button>
        </div>
      </div>
    </div>
  );
}
